/**
 * BankingSettings – finAPI Bankverbindungsverwaltung
 * Bankverbindung hinzufügen, Konten anzeigen, Transaktionen synchronisieren
 * KI-Kategorisierung mit Claude
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Building2, CreditCard, RefreshCw, Plus, Trash2, Search, 
  ArrowDownUp, CheckCircle, XCircle, AlertTriangle, Wallet,
  TrendingUp, TrendingDown, Clock, Link2, Unlink, Sparkles,
  CheckSquare, Square, Brain
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Kategorie-Farben
const KATEGORIE_COLORS = {
  rente: 'bg-emerald-100 text-emerald-700',
  grundsicherung: 'bg-green-100 text-green-700',
  buergergeld: 'bg-lime-100 text-lime-700',
  alg1: 'bg-teal-100 text-teal-700',
  pflegegeld: 'bg-cyan-100 text-cyan-700',
  wohngeld: 'bg-sky-100 text-sky-700',
  kindergeld: 'bg-blue-100 text-blue-700',
  gehalt: 'bg-indigo-100 text-indigo-700',
  miete: 'bg-red-100 text-red-700',
  energie: 'bg-orange-100 text-orange-700',
  versicherung: 'bg-amber-100 text-amber-700',
  krankenversicherung: 'bg-pink-100 text-pink-700',
  gesundheit: 'bg-rose-100 text-rose-700',
  lebensmittel: 'bg-yellow-100 text-yellow-700',
  mobilität: 'bg-violet-100 text-violet-700',
  kommunikation: 'bg-purple-100 text-purple-700',
  taschengeld: 'bg-fuchsia-100 text-fuchsia-700',
  schulden: 'bg-slate-100 text-slate-700',
  sonstiges: 'bg-gray-100 text-gray-600',
};

const KATEGORIE_LABELS = {
  rente: 'Rente',
  grundsicherung: 'Grundsicherung',
  buergergeld: 'Bürgergeld',
  alg1: 'ALG I',
  pflegegeld: 'Pflegegeld',
  wohngeld: 'Wohngeld',
  kindergeld: 'Kindergeld',
  gehalt: 'Gehalt',
  miete: 'Miete',
  energie: 'Energie',
  versicherung: 'Versicherung',
  krankenversicherung: 'Krankenversicherung',
  gesundheit: 'Gesundheit',
  lebensmittel: 'Lebensmittel',
  mobilität: 'Mobilität',
  kommunikation: 'Kommunikation',
  taschengeld: 'Taschengeld',
  schulden: 'Schulden',
  sonstiges: 'Sonstiges',
};

export default function BankingSettings() {
  // Status
  const [finapiStatus, setFinapiStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Verbindungen
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  
  // Transaktionen
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [selectedKonto, setSelectedKonto] = useState('');
  
  // Bank-Suche Dialog
  const [bankDialog, setBankDialog] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [banks, setBanks] = useState([]);
  const [searchingBanks, setSearchingBanks] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [connecting, setConnecting] = useState(false);
  
  // Konten für Verbindung
  const [konten, setKonten] = useState([]);
  const [selectedKontoForConnect, setSelectedKontoForConnect] = useState('');
  
  // KI-Kategorisierung
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [kiAnalyzing, setKiAnalyzing] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  
  // Auto-KI-Einstellungen
  const [autoKiEnabled, setAutoKiEnabled] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Initial laden
  useEffect(() => {
    loadStatus();
    loadConnections();
    loadKonten();
    loadTransactions(); // Transaktionen automatisch laden
    loadSettings();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/finapi/status`);
      const data = await res.json();
      setFinapiStatus(data);
      // Auto-KI Status aus Status-Response
      if (data.auto_ki_kategorisierung !== undefined) {
        setAutoKiEnabled(data.auto_ki_kategorisierung);
      }
    } catch (e) {
      console.error('Status laden fehlgeschlagen:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/finapi/settings`);
      const data = await res.json();
      if (data.success && data.settings) {
        setAutoKiEnabled(data.settings.auto_ki_kategorisierung || false);
      }
    } catch (e) {
      console.error('Einstellungen laden fehlgeschlagen:', e);
    }
  };

  const toggleAutoKi = async () => {
    setSavingSettings(true);
    try {
      const newValue = !autoKiEnabled;
      const res = await fetch(`${BACKEND_URL}/api/finapi/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_ki_kategorisierung: newValue })
      });
      
      const data = await res.json();
      if (data.success) {
        setAutoKiEnabled(newValue);
        toast.success(newValue 
          ? 'Auto-KI-Kategorisierung aktiviert' 
          : 'Auto-KI-Kategorisierung deaktiviert'
        );
      }
    } catch (e) {
      toast.error('Einstellung konnte nicht gespeichert werden');
    } finally {
      setSavingSettings(false);
    }
  };

  const loadConnections = async () => {
    setLoadingConnections(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/finapi/connections`);
      const data = await res.json();
      setConnections(data.connections || []);
    } catch (e) {
      console.error('Verbindungen laden fehlgeschlagen:', e);
    } finally {
      setLoadingConnections(false);
    }
  };

  const loadKonten = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/buchhaltung/konten`);
      const data = await res.json();
      setKonten(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Konten laden fehlgeschlagen:', e);
    }
  };

  const loadTransactions = async (kontoId = '') => {
    setLoadingTransactions(true);
    try {
      let url = `${BACKEND_URL}/api/buchhaltung/umsaetze`;
      if (kontoId) {
        url += `?konto_id=${kontoId}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      setTransactions(Array.isArray(data) ? data.slice(0, 50) : []);
    } catch (e) {
      console.error('Transaktionen laden fehlgeschlagen:', e);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Bank-Suche
  const searchBanks = async () => {
    if (!bankSearch.trim()) return;
    setSearchingBanks(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/finapi/banks?query=${encodeURIComponent(bankSearch)}`);
      const data = await res.json();
      setBanks(data.banks || []);
    } catch (e) {
      toast.error('Banksuche fehlgeschlagen');
    } finally {
      setSearchingBanks(false);
    }
  };

  // Bank verbinden
  const connectBank = async () => {
    if (!selectedBank || !selectedKontoForConnect) {
      toast.error('Bitte Bank und Konto auswählen');
      return;
    }
    
    setConnecting(true);
    try {
      const konto = konten.find(k => k.id === selectedKontoForConnect);
      const res = await fetch(`${BACKEND_URL}/api/finapi/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          klient_id: konto?.klient_id || 'system',
          konto_id: selectedKontoForConnect,
          bank_id: selectedBank.id
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        setBankDialog(false);
        setSelectedBank(null);
        setBankSearch('');
        setBanks([]);
        setSelectedKontoForConnect('');
        loadConnections();
      } else {
        toast.error(data.error || 'Verbindung fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Verbindung fehlgeschlagen');
    } finally {
      setConnecting(false);
    }
  };

  // Verbindung trennen
  const disconnectBank = async (connectionId) => {
    if (!window.confirm('Bankverbindung wirklich trennen?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/finapi/connections/${connectionId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Verbindung getrennt');
        loadConnections();
      }
    } catch (e) {
      toast.error('Trennen fehlgeschlagen');
    }
  };

  // Transaktionen synchronisieren
  const syncTransactions = async (connection) => {
    try {
      toast.info('Synchronisiere Transaktionen...');
      const res = await fetch(`${BACKEND_URL}/api/finapi/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          klient_id: connection.klient_id,
          konto_id: connection.konto_id
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(`${data.imported} neue Transaktionen importiert`);
        loadTransactions(selectedKonto);
        loadConnections();
      } else {
        toast.error(data.error || 'Synchronisation fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Synchronisation fehlgeschlagen');
    }
  };

  // Alle verbundenen Konten synchronisieren
  const syncAllConnections = async () => {
    for (const conn of connections.filter(c => c.status === 'verbunden')) {
      await syncTransactions(conn);
    }
  };

  // ==================== KI-KATEGORISIERUNG ====================
  
  // Transaktion auswählen/abwählen
  const toggleTransactionSelection = (txId) => {
    setSelectedTransactions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(txId)) {
        newSet.delete(txId);
      } else {
        newSet.add(txId);
      }
      return newSet;
    });
  };

  // Alle auswählen/abwählen
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(transactions.map(tx => tx.id)));
    }
    setSelectAll(!selectAll);
  };

  // Nur unkategorisierte auswählen
  const selectUncategorized = () => {
    const uncategorized = transactions.filter(
      tx => !tx.kategorie || tx.kategorie === 'sonstiges'
    );
    setSelectedTransactions(new Set(uncategorized.map(tx => tx.id)));
    toast.info(`${uncategorized.length} unkategorisierte Transaktionen ausgewählt`);
  };

  // KI-Analyse starten
  const startKiAnalyse = async () => {
    if (selectedTransactions.size === 0) {
      toast.error('Bitte wählen Sie mindestens eine Transaktion aus');
      return;
    }

    setKiAnalyzing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/finapi/ki-kategorisierung`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_ids: Array.from(selectedTransactions)
        })
      });

      const data = await res.json();
      
      if (res.status === 503) {
        toast.error('KI nicht verfügbar: Bitte LLM-Guthaben aufladen');
        return;
      }
      
      if (!res.ok) {
        toast.error(data.detail || 'KI-Analyse fehlgeschlagen');
        return;
      }

      if (data.success) {
        toast.success(
          <div className="space-y-1">
            <p className="font-medium">KI-Kategorisierung abgeschlossen!</p>
            <p className="text-sm">{data.aktualisiert} von {data.analysiert} Transaktionen aktualisiert</p>
          </div>
        );
        
        // Transaktionen neu laden
        loadTransactions(selectedKonto === '_all' ? '' : selectedKonto);
        setSelectedTransactions(new Set());
        setSelectAll(false);
      } else {
        toast.error(data.message || 'KI-Analyse fehlgeschlagen');
      }
    } catch (e) {
      console.error('KI-Analyse Fehler:', e);
      toast.error('KI-Analyse fehlgeschlagen');
    } finally {
      setKiAnalyzing(false);
    }
  };

  // ==========================================================

  const formatBetrag = (betrag) => {
    const num = parseFloat(betrag) || 0;
    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(Math.abs(num));
    return num >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const formatDatum = (datum) => {
    if (!datum) return '-';
    return new Date(datum).toLocaleDateString('de-DE');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="banking-settings">
      {/* Header mit Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">Banking Integration</h3>
            <p className="text-sm text-slate-500">
              finAPI Bankverbindungen verwalten und Transaktionen synchronisieren
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {finapiStatus?.mode === 'mock' ? (
            <span className="flex items-center gap-1.5 text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
              <AlertTriangle className="w-4 h-4" />
              Mock-Modus
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full">
              <CheckCircle className="w-4 h-4" />
              Live
            </span>
          )}
        </div>
      </div>

      {/* Mock-Modus Info */}
      {finapiStatus?.mode === 'mock' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800">
                <strong>Mock-Modus aktiv:</strong> Keine finAPI-Credentials konfiguriert. 
                Es werden simulierte Bankdaten und Transaktionen verwendet.
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Für echte Bankanbindung: FINAPI_CLIENT_ID und FINAPI_CLIENT_SECRET in .env setzen.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Auto-KI-Kategorisierung Einstellung */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                Auto-KI-Kategorisierung
                {autoKiEnabled && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    Aktiv
                  </span>
                )}
              </h4>
              <p className="text-sm text-slate-500">
                Claude kategorisiert neue Transaktionen automatisch bei jeder Synchronisation
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {savingSettings && (
              <RefreshCw className="w-4 h-4 animate-spin text-purple-500" />
            )}
            <Switch
              checked={autoKiEnabled}
              onCheckedChange={toggleAutoKi}
              disabled={savingSettings}
              data-testid="auto-ki-toggle"
            />
          </div>
        </div>
        {autoKiEnabled && (
          <div className="mt-3 pl-13 text-xs text-purple-600 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            Bei jeder Sync werden unkategorisierte Transaktionen automatisch analysiert
          </div>
        )}
      </div>

      {/* Verbindungen */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-slate-700 flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Bankverbindungen
          </h4>
          <div className="flex gap-2">
            {connections.length > 0 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={syncAllConnections}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Alle synchronisieren
              </Button>
            )}
            <Dialog open={bankDialog} onOpenChange={setBankDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Bank verbinden
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Bankverbindung hinzufügen</DialogTitle>
                  <DialogDescription>
                    Suchen Sie Ihre Bank und verbinden Sie ein Konto.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Bank-Suche */}
                  <div className="space-y-2">
                    <Label>Bank suchen</Label>
                    <div className="flex gap-2">
                      <Input
                        value={bankSearch}
                        onChange={(e) => setBankSearch(e.target.value)}
                        placeholder="Name oder BLZ eingeben..."
                        onKeyDown={(e) => e.key === 'Enter' && searchBanks()}
                        data-testid="bank-search-input"
                      />
                      <Button onClick={searchBanks} disabled={searchingBanks}>
                        {searchingBanks ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Bank-Ergebnisse */}
                  {banks.length > 0 && (
                    <div className="space-y-2">
                      <Label>Bank auswählen</Label>
                      <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
                        {banks.map((bank) => (
                          <button
                            key={bank.id}
                            onClick={() => setSelectedBank(bank)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                              selectedBank?.id === bank.id
                                ? 'bg-emerald-100 border-emerald-300 border'
                                : 'hover:bg-slate-50'
                            }`}
                            data-testid={`bank-option-${bank.id}`}
                          >
                            <div className="font-medium">{bank.name}</div>
                            <div className="text-xs text-slate-500">
                              BLZ: {bank.blz} • BIC: {bank.bic}
                              {bank.is_mock && ' • (Mock)'}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Konto auswählen */}
                  {selectedBank && (
                    <div className="space-y-2">
                      <Label>Konto zuordnen</Label>
                      <Select value={selectedKontoForConnect} onValueChange={setSelectedKontoForConnect}>
                        <SelectTrigger data-testid="konto-select">
                          <SelectValue placeholder="Konto auswählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          {konten.map((konto) => (
                            <SelectItem key={konto.id} value={konto.id}>
                              {konto.bezeichnung} {konto.iban && `(${konto.iban.slice(-4)})`}
                            </SelectItem>
                          ))}
                          {konten.length === 0 && (
                            <SelectItem value="_none" disabled>
                              Keine Konten vorhanden
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Wählen Sie ein bestehendes Konto aus der Buchhaltung.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setBankDialog(false)}>
                    Abbrechen
                  </Button>
                  <Button 
                    onClick={connectBank} 
                    disabled={!selectedBank || !selectedKontoForConnect || connecting}
                    data-testid="connect-bank-btn"
                  >
                    {connecting ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4 mr-2" />
                    )}
                    Verbinden
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Verbindungsliste */}
        {loadingConnections ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : connections.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p>Keine Bankverbindungen</p>
            <p className="text-xs text-slate-400 mt-1">
              Klicken Sie auf "Bank verbinden", um eine Verbindung hinzuzufügen.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {connections.map((conn) => (
              <div
                key={conn.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                data-testid={`connection-${conn.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg border flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{conn.bank_name}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className={`px-1.5 py-0.5 rounded ${
                        conn.status === 'verbunden' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {conn.status}
                      </span>
                      {conn.ist_mock && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Mock</span>
                      )}
                      {conn.letzte_sync && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDatum(conn.letzte_sync)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => syncTransactions(conn)}
                    className="gap-1"
                    data-testid={`sync-btn-${conn.id}`}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Sync
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => disconnectBank(conn.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`disconnect-btn-${conn.id}`}
                  >
                    <Unlink className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Transaktionen */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-slate-700 flex items-center gap-2">
            <ArrowDownUp className="w-4 h-4" />
            Transaktionen
            {selectedTransactions.size > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                {selectedTransactions.size} ausgewählt
              </span>
            )}
          </h4>
          <div className="flex items-center gap-2">
            {/* KI-Analyse Buttons */}
            {transactions.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectUncategorized}
                  className="gap-1.5 text-amber-600 border-amber-200 hover:bg-amber-50"
                  data-testid="select-uncategorized-btn"
                >
                  <Square className="w-3.5 h-3.5" />
                  Unkategorisierte
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={startKiAnalyse}
                        disabled={selectedTransactions.size === 0 || kiAnalyzing}
                        size="sm"
                        className="gap-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                        data-testid="ki-analyse-btn"
                      >
                        {kiAnalyzing ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        KI-Analyse
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Claude analysiert die ausgewählten Transaktionen und schlägt passende Kategorien vor</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
            <Select value={selectedKonto} onValueChange={(v) => {
              setSelectedKonto(v);
              setSelectedTransactions(new Set());
              setSelectAll(false);
              loadTransactions(v === '_all' ? '' : v);
            }}>
              <SelectTrigger className="w-48" data-testid="transaction-filter">
                <SelectValue placeholder="Alle Konten" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Alle Konten</SelectItem>
                {konten.map((konto) => (
                  <SelectItem key={konto.id} value={konto.id}>
                    {konto.bezeichnung}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadTransactions(selectedKonto === '_all' ? '' : selectedKonto)}
              disabled={loadingTransactions}
            >
              <RefreshCw className={`w-4 h-4 ${loadingTransactions ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Transaktionsliste */}
        {loadingTransactions ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Wallet className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p>Keine Transaktionen</p>
            <p className="text-xs text-slate-400 mt-1">
              Synchronisieren Sie eine Bankverbindung, um Transaktionen zu sehen.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="pb-2 pr-2 w-8">
                    <Checkbox
                      checked={selectAll}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="pb-2 font-medium">Datum</th>
                  <th className="pb-2 font-medium">Beschreibung</th>
                  <th className="pb-2 font-medium">Kategorie</th>
                  <th className="pb-2 font-medium text-right">Betrag</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, idx) => (
                  <tr 
                    key={tx.id || idx} 
                    className={`border-b border-slate-100 hover:bg-slate-50 ${
                      selectedTransactions.has(tx.id) ? 'bg-purple-50' : ''
                    }`}
                    data-testid={`transaction-row-${idx}`}
                  >
                    <td className="py-2.5 pr-2">
                      <Checkbox
                        checked={selectedTransactions.has(tx.id)}
                        onCheckedChange={() => toggleTransactionSelection(tx.id)}
                        data-testid={`tx-checkbox-${idx}`}
                      />
                    </td>
                    <td className="py-2.5 text-slate-600">
                      {formatDatum(tx.datum)}
                    </td>
                    <td className="py-2.5">
                      <div className="max-w-xs">
                        <p className="font-medium text-slate-800 truncate">
                          {tx.empfaenger_auftraggeber || '-'}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {tx.verwendungszweck || '-'}
                        </p>
                      </div>
                    </td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          KATEGORIE_COLORS[tx.kategorie] || KATEGORIE_COLORS.sonstiges
                        }`}>
                          {KATEGORIE_LABELS[tx.kategorie] || tx.kategorie || 'Sonstiges'}
                        </span>
                        {tx.ki_kategorisiert && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Brain className="w-3.5 h-3.5 text-purple-500" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">
                                  KI-kategorisiert ({Math.round((tx.ki_konfidenz || 0) * 100)}% Konfidenz)
                                  {tx.ki_begruendung && <><br />{tx.ki_begruendung}</>}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </td>
                    <td className={`py-2.5 text-right font-medium ${
                      tx.betrag >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      <span className="flex items-center justify-end gap-1">
                        {tx.betrag >= 0 ? (
                          <TrendingUp className="w-3.5 h-3.5" />
                        ) : (
                          <TrendingDown className="w-3.5 h-3.5" />
                        )}
                        {formatBetrag(tx.betrag)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {transactions.length >= 50 && (
              <p className="text-xs text-slate-500 text-center mt-3">
                Zeige die letzten 50 Transaktionen
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
